import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { getHumanizedDuration } from '@libs/utils';
import { TransportType } from '@libs/trip-planner-interfaces';
import type {
  HotelIngredientResponseWithStaticInfo,
  IngredientId,
  OwnStayIngredientResponseWithStaticInfo,
  OwnTransportIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';
import type { RouteWithDetails } from '@libs/maps';
import { ConnectionType } from '@libs/maps';

import type {
  ItineraryNoTransportRelatedSegment,
  ItinerarySimpleTransportSegment,
  ItinerarySimpleTransportSegmentType,
} from '../../models';
import { ItinerarySegmentType } from '../../models';

import { getRoute } from './tripOverviewHotelRelatedSegmentsLogic';
import { getCountryText } from './tripOverviewLocationSegmentsLogic';

export interface GetTripOverviewNoTransportSegmentParams {
  ingredientId: IngredientId;
  ingredientIndex: number | undefined;
  isReturnTransport: boolean;
  isLastSegment: boolean;
  isUserFromBookingPage: boolean;
  isUserFromStena: boolean;
  displayText: string;
  hasUserSelectedTransport: boolean;
  mainPolygonFullName?: string;
}

export function getTripOverviewNoTransportSegment(
  params: GetTripOverviewNoTransportSegmentParams
): ItineraryNoTransportRelatedSegment {
  if (
    params.ingredientIndex === 0 &&
    !params.isUserFromBookingPage &&
    !params.hasUserSelectedTransport
  ) {
    if (params.isUserFromStena) {
      return {
        segmentType: ItinerarySegmentType.StenaUpsellNoTransport,
        ingredientId: params.ingredientId,
      };
    } else {
      return {
        segmentType: ItinerarySegmentType.UpsellNoTransport,
        ingredientId: params.ingredientId,
        destination: getCountryText(
          params.displayText,
          params.mainPolygonFullName
        ),
      };
    }
  }

  return {
    segmentType: ItinerarySegmentType.NoTransport,
    ingredientId: params.ingredientId,
    isReturnTransport: params.isReturnTransport,
    isLastSegment: params.isLastSegment,
  };
}

interface GetTripOverviewSimpleTransportSegmentParams {
  ingredient: OwnTransportIngredientResponseWithStaticInfo;
  hotelRelatedIngredient:
    | HotelIngredientResponseWithStaticInfo
    | OwnStayIngredientResponseWithStaticInfo
    | HotelIngredient
    | OwnStayIngredient;
  calculatedRoute: RouteWithDetails;
  isLastSegment: boolean;
}

export function getTripOverviewSimpleTransportSegment(
  params: GetTripOverviewSimpleTransportSegmentParams
): ItinerarySimpleTransportSegment {
  const ingredient = params.ingredient;
  const hotelIngredient = params.hotelRelatedIngredient;

  const isHotelIngredientResponseWithStaticInfo =
    'staticInfo' in hotelIngredient;

  const route =
    isHotelIngredientResponseWithStaticInfo && hotelIngredient.staticInfo
      ? getRoute(hotelIngredient.staticInfo, params.calculatedRoute)
      : params.calculatedRoute.at(-1);
  return {
    segmentType: getItinerarySegmentTypeFromTransportType(
      ingredient.ownTransportType
    ),
    ingredientId: params.ingredient.ingredientId,
    isLastSegment: params.isLastSegment,
    transport:
      route?.distanceInKilometers && route.expectedTravelTimeInMinutes
        ? {
            distance: `${route.distanceInKilometers.toFixed()}km`,
            time: getHumanizedDuration(
              route.expectedTravelTimeInMinutes,
              'minute'
            ),
          }
        : undefined,
  };
}

export function isSimpleTransport(connectionType: ConnectionType): boolean {
  const simpleTransports = [
    ConnectionType.CAR,
    ConnectionType.BICYCLE,
    ConnectionType.WALK,
  ];

  return simpleTransports.includes(connectionType);
}

export function getItinerarySegmentTypeFromTransportType(
  transport: TransportType
): ItinerarySimpleTransportSegmentType {
  switch (transport) {
    case TransportType.Car:
      return ItinerarySegmentType.Car;
    case TransportType.Bicycle:
      return ItinerarySegmentType.Cycle;
    case TransportType.Walk:
      return ItinerarySegmentType.Walk;
    default:
      throw new Error('No Transport in hotel Ingredient');
  }
}
